/**
 * 路由中间键
 * https://nuxt.com/docs/getting-started/routing
 */

import domain from '../server-config/domain.config'
import { appendAfterSaleSite, getAfterSaleSite } from '@/utils/after-sale-site'
import { refreshToken } from '@/api/models/userInfo'
import { $http } from '@/api/request'
import Qs from 'qs'
import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router'
import { getDefaultSiteTitle } from '@/utils/site-title'
import { ensureWapListingDetailVersion, isListingDetailVersionRoute } from '@/utils/wap-listing-detail-version'

/**
 * 兼容 老K 系统路由
 * @param route 
 */
const KWapRedirect = (route: any): string => {
  const { path } = route
  
  let url: string = ''

  if(/^(\/new\/goods\/|\/goods\/|\/product\/)/.test(path)) {
    const skuId = path.split('/').pop()

    if(!skuId) {
      url = '/'
    } else {
      if(/^[0-9]$/.test(skuId)) {
        url = `/listing/${skuId}?type=sku`
      } else {
        const [_id] = skuId.split('-')

        url = `/listing/${_id}?type=sku`
      }
    }
  } else if(/^\/search-result/.test(path)) { // 新搜索页 /search?q=xxx
    const _query = { ...route.query }

    if(!_query.keyword && !_query.q && !_query.brandIds) {
      url = useIsMobile().value ? '/search-input' : '/'
    } else {
      const q = _query.keyword || _query.q || ''
      delete _query.keyword

      const _queryStr = Qs.stringify({ q, page: 1, ..._query }, { arrayFormat: 'repeat' })
      url = `/search?${_queryStr}`
    }
  }else if(/\/new\/commoditysearch/.test(path)) {
    url = route.query.q 
      ? `/search?q=${route.query.q}&source=searchProduct|enterSearch|${route.query.q}`
      : '/search-input'
  } else if(/\/search\//.test(path)) {
    const qs = route.path.split('/')

    if(qs.length < 3) {
      url = useIsMobile().value ? '/search-input' : '/'
    } else {
      const keyword = qs.pop()

        url = keyword
          ? `/search?q=${keyword}&source=searchProduct|enterSearch|${keyword}`
          : useIsMobile().value
            ? '/search-input'
            : '/'
    }
  } else if(/\/new\/store\//.test(path)) {
    const storeId = route.path.split('/').pop()

    url = storeId ? `/store/${storeId}` : '/'
  } else if(/\/new\/flash-sales|\/flashSale/.test(path)) {
    url = '/flash-sales'
  } else if(/\/usercenter/.test(path)) {
    url = '/user-center'
  } else if(/\/new\/cart|\/cart/.test(path)) {
    url = '/shopping-cart'
  } else if(/\/new\/account\/messages/.test(path)) {
    url = '/message'
  } else if(/\/setting$/.test(path)) {
    url = '/setting/account'
  } else if(path === '/orderquery') {
    url = '/logistics/orderquery'
  } else if(/^\/new\/$/.test(path)) {
    url = '/'
  } else if(/\/message$/.test(path) && !useIsMobile().value) {
    url = '/message/station-letters'
  } else if(/^\/order$/.test(path)) {
    url = `${domain.OLD_K_URL}/order`
  } else if(/^\/afterSale$/.test(path)) {
    url = `${domain.OLD_K_URL}/afterSale`
  } else if(/^\/closeAccount$/.test(path)) {
    url = '/logout-account/close-account'
  } else if(/^\/after-sale/.test(path)) {
    // 售后
    const site = getAfterSaleSite(domain.SELLER_H5_URL)
    url = appendAfterSaleSite(`/sellerApp/#${route.href}`, site)
  }
  return url
}

/**
 * 全局SEO
 * @param path 
 * @returns 
 */
function setGlobalSEOHead(path:string) {
  const seoConfig = useGlobalJsonData().value.seo?.data
  if(!seoConfig?.globalConfig) {
    useHead({ title: getDefaultSiteTitle() })
    return
  }
  // 排除 详情页、搜乺结果页/类目结果页、首页，其余页面使用通用配置项
  if(/^(^(\/listing\/|\/search\?))/.test(path)) {
    return
  }

  const { title: seoTitle, metaDescription, metaTitle, metaKeywords } = seoConfig.globalConfig

  useHead({
    title: seoTitle || getDefaultSiteTitle(),
    meta: [
      { name: 'title', content: metaTitle },
      { name: 'description', content: metaDescription },
      { name: 'keywords', content: metaKeywords }
    ]
  })
}

/** 清空用户Token */
function clearUserInfo() {
  useUserInfo().value =null
  useCookie('UserInfo').value = ''
}

// 请求超时时间
// const requestTimeout = 120 * 1000
// 记录一下 Token 刷新的状态
let refreshStatus = -1 // 0 请求中 -1 初始状态，2 失败
// 与Token过期时间间隔的时间
const refreshTime = 60 * 10 * 1000 // 10分钟
async function hasRefreshToken() {
  const userInfo = useUserInfo().value as IUserInfo

  if(!userInfo || !userInfo.refreshToken) {
    return
  }

  const { accessTokenTimeout } = userInfo
  const tokenExpirationTime = accessTokenTimeout * 1000
  const currentTime = new Date().getTime() + refreshTime

  const isAndroid: boolean = useIsAndroid().value
  const isFlutter: boolean = useIsFlutterApp().value

  if (refreshStatus === 0) {
    return
  }
  // 更新标志
  refreshStatus = 0
  if (
    currentTime > tokenExpirationTime
  ) {
    try {
      if (isAndroid && process.client) {
        window.androidWebObject?.refreshToken()
      } else if(isFlutter && process.client) {
        window.flutter_inappwebview.callHandler('refreshToken')
      } else {
        const newTokenRes = await refreshToken().catch((e: any) => {
          console.error('*****************newTokenRes: ', e)
        })
        const { code, data: newToken } = newTokenRes

        if (code === 0) {
          userInfo.refreshToken = newToken.refreshToken
          userInfo.refreshTokenTimeout = newToken.refreshTokenTimeout
          userInfo.accessToken = newToken.accessToken
          userInfo.accessTokenTimeout = newToken.accessTokenTimeout

          return userInfo
        }

        refreshStatus = -1
      }
    } catch (e) {
      console.error('token刷新失败', e)

      refreshStatus = 2
      clearUserInfo()
    }
  } else {
    // 重置标志
    // isRefreshToken = false
    refreshStatus = -1
    // currentRefreshCount = 0
  }
}

/**
 * 有一些页面需要动态区分 PC/WAP，以控制页面大小
 * @param to 
 * @returns 
 */
const customDynamicRoutingConfig: IAnyObject = {
  'listing': { cname: 'listing-detail', pcComponent: () => import('@/pages-listing-detail/pc.vue'), wapComponent: () => import('@/pages-listing-detail/wap.vue') },
  'home': { cname: 'home', pcComponent: () => import('@/pages-home/pc.vue'), wapComponent: () => import('@/pages-home/wap.vue') },
  'pvHome': { cname: 'pv-home', pcComponent: () => import('@/pages-home/pvHome.vue'), wapComponent: () => import('@/pages-home/pvHome.vue') }
}
async function customDynamicRouting(
  to: RouteLocationNormalized
): Promise<RouteRecordNormalized | undefined> {
  let cpms
  
  if(/^\/event\/[^/]+\/?$/.test(to.path) && !useIsMobile().value) {
    // Preserve the deployed WAP page; only desktop events use the new layout.
    cpms = { cname: 'event-pageSlug', pcComponent: () => import('@/pages-event/pc.vue') }
    to.meta.layout = false
  } else if(to.path === '/') {
    cpms = customDynamicRoutingConfig['home']
  } else if(/^\/pvHome/.test(to.path)) {
    cpms = customDynamicRoutingConfig['pvHome']
  } else if(/^\/listing\//.test(to.path)) {
    cpms = customDynamicRoutingConfig['listing']

    if(!useIsMobile().value) {
      to.meta.notLoading = true
    }
  }
  
  if(!cpms) {
    return
  }

  let { pcComponent, wapComponent, cname } = cpms
  const components = useIsMobile().value ? await wapComponent() :await pcComponent()

  return {
    path: to.path,
    name: cname,
    meta: to.meta,
    props: { "default": false },
    children: [],
    instances: {},
    redirect: undefined,
    beforeEnter: undefined,
    aliasOf: undefined,
    leaveGuards: new Set(),
    updateGuards: new Set(),
    enterCallbacks: {},
    components
  }
}

const pageLoading = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if(to.meta?.notLoading) {
    useRouterLoading().value = false
    return
  }

  if(to.name !== from.name && process.client) {
    // 页面奢姿 notLoading 后，刷新当前页面不会展示 loading
    // 但是从别的页面进入这个页面还是会显示loading
    // useRouterLoading().value = true
    // setTimeout(() => {
    //   useRouterLoading().value = false
    // }, 2 * 1000)
  }
}
export default defineNuxtRouteMiddleware(async (to, from) => {
  console.log(`Route: ${from.path} -> ${to.path}`)
  if(/undefined/.test(to.path)) {
    console.info(`Path undefined: ${from.path} -> ${to.path}`)
    
    return navigateTo('/')
  }

  if(process.client) {
    const ssqEl = document.getElementById('s-chat-plugin')

    ssqEl && (ssqEl.style.display = 'none')
  }

  /** 深链跳转失败 - 打开 Kilimall APP 谷歌应用市场 */
  // if(/deepLink/.test(to.path)) {
  //   return navigateTo('https://play.google.com/store/apps/details?id=net.kilimall.shop', { external: true })
  // }

  // 获取当前域名，根据域名设置默认国家
  const headers = useRequestHeaders();
  // 国家域名映射，新增国家时在此添加
  const COUNTRY_HOST_MAP = [
    { pattern: /ug/i, code: 'UG', regionId: '8' },
    // { pattern: /ng/i, code: 'NG', regionId: 'xx' },
  ]
  if (headers?.host) {
    const country = COUNTRY_HOST_MAP.find(c => c.pattern.test(headers.host!))
    if (country) {
      // 用户第一次访问时设置默认国家, 避免node层国家不对， 到APP.vue会再次更新数据的
      useCurrentCountryCode().value = country.code
      useCurrentCountry().value = country.regionId
    }
  }
  

  if(
    (headers && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(headers['user-agent']))
      || to.query?.t === 'm'
  ) {
    useIsMobile().value = true
  }
  if ((headers && /FBAV|FBAN/i.test(headers['user-agent'])) || to.query.ksa === 'facebook') {
    isFacebook().value = true
  }
  if ((headers && /WhatsApp/i.test(headers['user-agent'])) || to.query.ksa === 'whatsapp') {
    isWhatsApp().value = true
  }
  if ((headers && /TikTok/i.test(headers['user-agent'])) || to.query.ksa === 'tiktok') {
    isTiktok().value = true
  }

  if(/\/user-center/.test(to.path) && !useIsMobile().value) {
    return navigateTo('/orders')
  }

  /** 自定义动态路由规则检测 */
  const routeComponent: any = await customDynamicRouting(to)
  pageLoading(to, from)
  if(process.server) {

    // 新增：检查/listing/路由并触发product-checked接口
    if(/^\/listing\//.test(to.path)) {
      const userInfo = useUserInfo().value
      
      try {
        // 根据用户是否登录选择不同的检查方式
        if(userInfo?.userId) {
          // 已登录用户检查
          const ret = await $fetch('/api/product-checked', {
            method: 'POST',
            body: {
              type: 1,
              dataInfo: { userId: userInfo.userId }
            }
          })
          if(ret.needVerify) {
            let path = useRoute().fullPath
            return navigateTo('/checkCode?redirect=' + encodeURI(path))
          }
          
        } else {
          const userAgent = headers['user-agent'] || ''
          const ip =
            headers['true-client-ip'] ||
            (headers['x-forwarded-for'] ? headers['x-forwarded-for'].split(',')[0] : null) ||
            headers['bigo-x-client-source-ip'] ||
            headers['x-real-ip'] || ''
          const ipWhiteList = [
            '52.68.76.31',
            '103.4.201.4',
            '197.248.231.190',
            '159.138.162.190',
            '42.48.104.67',
            '119.8.100.74',
            '66.249.74.96',
            '66.249.74.97',
            '66.249.74.98',
            '66.249.74.103',
            '66.249.74.108',
            '66.249.74.105',
            '66.249.74.101',
            '66.249.74.107',
            '66.249.74.99',
            '66.249.74.34'
          ]
          const uAWhiteList = [
            '+http://www.google.com/bot.html',
            '+https://webmaster.petalsearch.com/site/petalbot',
            '+http://www.google.com/bot.html',
            '+http://yandex.com/bots',
            '+http://www.bing.com/bingbot.htm',
            '+http://www.facebook.com/externalhit_uatext.php',
            '+http://omgili.com'
          ]
          // IP白名单匹配 、UA白名单匹配
          if(ipWhiteList.find(item => ip.indexOf(item) > -1) || uAWhiteList.find(item => userAgent.indexOf(item) > -1)) {
            // 白名单内， 不拦截
            console.info('WhiteList', ip, userAgent)
          } else {
            // 未登录用户检查
            const ret = await $fetch('/api/product-checked', {
              method: 'POST',
              body: {
                type: 2,
                dataInfo: { 
                  ip: ip,
                  userAgent: userAgent
                }
              }
            })
            if(ret.needVerify) {
              let path = useRoute().fullPath
              return navigateTo('/login?redirect=' + encodeURI(path))
            }
          }
        }
      } catch (e) {
        console.error('product-checked接口调用失败:', e)
      }
    }

    // 多国切换登录
    if(/\/srs_cookie/.test(to.fullPath) && to.query.userKey) {
      try {
        const { regionId, userInfo } = await $http.get({ url: `http://${headers.host}/api/get-token?userKey=${to.query.userKey}` }).then((r: any) => r.data)

        useCookie('regionId').value = regionId
        useCookie('UserInfo').value = userInfo || {}
        useUserInfo().value = userInfo || {}
      } catch(e) {
        console.error(e)
      }
      return navigateTo('/')
    }

    // Token 刷新逻辑
    const userinfo = await hasRefreshToken()

    if(userinfo) {
      useUserInfo().value = userinfo
      useCookie<IUserInfo>('UserInfo').value = userinfo as IUserInfo
    }
  }  

  //判断webp
  if(headers && headers['accept'] && headers['accept'].toString().indexOf('image/webp') > -1) {
    IsWebp().value = true
  }

  setGlobalSEOHead(to.path)

  // 老 K系统 路由兼容检测
  const newUrl = KWapRedirect(to)

  if(newUrl) {
    console.info("路由兼容检测: ", newUrl)
    
    return navigateTo(newUrl, { external: true })
  }

  if (process.client && isListingDetailVersionRoute(to.path)) {
    await ensureWapListingDetailVersion()
  }

  if(!useUserInfo().value) {
    if(to.path === '/user-center') {
      // 用户未登陆， 不允许进入个人中心
      return navigateTo('/login?redirect=user-center')
    }
    if(/\/promotion-listings\//.test(to.path)) {
      return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
    }
  }

  if(to.path === '/favorite-store' && !useIsMobile().value){
    return navigateTo('/wish-list?tab=shops')
  }

  // 针对 /listing/listingId#skuId（/listing/2301125#19369474）情况处理
  if(/\/listing\//.test(to.path)) {
    const regRes= to.fullPath.match(/\/listing\/([0-9#]*)/)
    
    if(regRes && regRes[1]) {
      const [listingId, skuId] = regRes[1].split('#')

      if(listingId && skuId) {
        return navigateTo(`/listing/${listingId}?skuId=${skuId}`)
      } 
    }
  }

  if(routeComponent) {
    to.matched = [routeComponent]
    return
  }

  // 没有匹配到任何路由，跳转到404页面（该页面不会被 Google 索引）
  if(useRouter().resolve(to.path).matched.length <= 0) {
    return navigateTo('/404')
  }
})
 
